// @ts-strict-ignore
import type { SerializedStyles, Theme } from "@emotion/react";
import type { ReactNode } from "react";

import { Typography, Link } from "@aviary";
import { emersonRoutes } from "@emerson/data/emersonRoutes";
import { AnalyticsEvent } from "@shared/hooks/useAnalyticsLogger/AnalyticsEvent";
import { EmersonSearchApplyFilterEntryPoint } from "@shared/hooks/useAnalyticsLogger/EmersonAnalyticsEntryPoint";
import {
  useAnalyticsLogger,
  FilterType,
} from "@shared/hooks/useAnalyticsLogger/useAnalyticsLogger";
import { useSharedGlobalConfig } from "@shared/hooks/useSharedGlobalConfig/useSharedGlobalConfig";
import type { MenuCategory, MenuSubCategory } from "@shared/types/graphqlGenerated";

import * as styles from "./CategoryLink.styles";

interface Props {
  category: MenuCategory | MenuSubCategory;
  linkStyles?:
    | SerializedStyles
    | (SerializedStyles | ((theme: Theme) => SerializedStyles))[]
    | ((theme: Theme) => SerializedStyles);
  hasOnlyOneSubcategory?: boolean;
  isHoverUnderLined?: boolean;
  sideMenuCallbacks?: {
    onFocus: () => void;
    onBlur: () => void;
  };
  isSubcategory?: boolean;
  categoryName: string;
  children?: ReactNode;
  role?: string;
  onClick?: (event: React.MouseEvent) => void;
  isMarginless?: boolean;
  isSecondaryWeight?: boolean;
}

const CategoryLink = ({
  category,
  linkStyles,
  hasOnlyOneSubcategory,
  sideMenuCallbacks,
  isSubcategory,
  categoryName,
  role,
  onClick,
  isHoverUnderLined = false,
  isMarginless,
  isSecondaryWeight,
  children,
}: Props) => {
  const logAnalyticsEvent = useAnalyticsLogger();
  const { isUnauthenticated } = useSharedGlobalConfig();

  const getCategoryPath = () => {
    const { id, name } = category;

    return emersonRoutes.getCategory(name, id);
  };

  if (!category) return null;

  const selectedSubcategory = isSubcategory ? category.name : undefined;
  const subcategoryName =
    hasOnlyOneSubcategory && "subCategories" in category
      ? category.subCategories?.[0]?.name
      : selectedSubcategory;

  const onMenuClick = (e: React.MouseEvent) => {
    logAnalyticsEvent({
      eventName: isUnauthenticated
        ? AnalyticsEvent.EMERSON_UNAUTH_SHOP_MENU_CLICK_TO_NAVIGATE
        : AnalyticsEvent.EMERSON_SHOP_MENU_CLICK_TO_NAVIGATE,
      payload: {
        origin_url: location.pathname,
        category_name: categoryName,
        subcategory_name: subcategoryName,
        content_name: category.name,
        automatic_event: false,
      },
    });

    logAnalyticsEvent({
      eventName: AnalyticsEvent.EMERSON_SEARCH_APPLY_FILTER,
      payload: {
        entry_point: EmersonSearchApplyFilterEntryPoint.SHOP_MENU,
        content: category.name,
        filter_type: FilterType.CATEGORY,
        search_version: 2,
      },
    });

    onClick?.(e);
  };

  return (
    <Link
      to={getCategoryPath()}
      isColor="system"
      isUnderLined={false}
      isHoverUnderLined={isHoverUnderLined}
      css={linkStyles}
      onClick={onMenuClick}
      aria-label={category?.name}
      role={role}
      onFocus={sideMenuCallbacks?.onFocus}
      onBlur={sideMenuCallbacks?.onBlur}
    >
      <Typography
        type={isSubcategory ? "footnote" : "body"}
        css={!!isSubcategory && styles.textStyle}
        isSecondaryWeight={isSecondaryWeight}
        isMarginless={isMarginless}
      >
        {category.name}
      </Typography>
      {children}
    </Link>
  );
};

export { CategoryLink };
